import { accessSync, constants, lstatSync, readFileSync, realpathSync } from "node:fs";
import { isAbsolute, relative } from "node:path";
import * as z from "zod";
import type { SessionInitCommand } from "@skaile/workspaces/types";
import { prepareNativePolicy } from "../../bridge/src/codex/native-policy.js";
import { codexInstanceEnvironment } from "../../bridge/src/codex/instance-state.js";
import { parseCodexCredential } from "./codex-init.js";
import type { CodexNativeApprovalHandlers } from "./codex-tools.js";
import type { AgentSessionConfig } from "./session-builder.js";

const text = z.string().min(1).max(4096);
const timeout = z.number().int().positive().max(1_800_000).optional();
const zHost = z
  .object({
    schemaVersion: z.literal(1),
    sessionId: z.string().min(1).max(128),
    runtime: z
      .object({
        executable: text,
        executableArgs: z.array(text).max(64).optional(),
        privateDirectory: text,
        privateRoot: text,
        environment: z.record(z.string(), z.string()),
        instructionFiles: z.array(text).max(64).optional(),
        skillsDirectory: text.optional(),
        requestTimeoutMs: timeout,
        turnTimeoutMs: timeout,
        interactionTimeoutMs: timeout,
      })
      .strict(),
    requiredTools: z.array(z.string().min(1).max(256)).min(1).max(1024),
    approvalPolicy: z.enum(["never", "on-request", "untrusted"]),
    sandboxMode: z.enum(["read-only", "workspace-write"]),
    reasoningEffort: z.enum(["minimal", "low", "medium", "high", "xhigh"]),
    subpromptModels: z
      .object({
        small: text,
        deep: text,
        available: z.array(text).min(1).max(1024),
      })
      .strict(),
  })
  .strict();

/** Explicit launcher inputs. Credentials and project configuration never supply these fields. */
export type ManagedCodexHostOptions = z.infer<typeof zHost> & {
  approvals?: CodexNativeApprovalHandlers;
};

const refused = () => new Error("CODEX_MANAGED_BOOTSTRAP_INVALID");
const overlaps = (a: string, b: string) => {
  const within = (child: string, parent: string) => {
    const rel = relative(parent, child);
    return rel === "" || (!rel.startsWith("..") && !isAbsolute(rel));
  };
  return within(a, b) || within(b, a);
};

/** Compatibility defaults; the managed driver owns native consent and replaces these hooks. */
const denyNativeGrants: CodexNativeApprovalHandlers = {
  "item/commandExecution/requestApproval": async () => ({ decision: "decline" }),
  "item/fileChange/requestApproval": async () => ({ decision: "decline" }),
  "item/permissions/requestApproval": async () => ({ permissions: {}, scope: "turn" }),
};

/** Validate before feature advertisement. This checks usable host inputs, not native qualification. */
export function validateManagedCodexHost(
  input: ManagedCodexHostOptions,
  projectDir: string,
): ManagedCodexHostOptions {
  try {
    const { approvals, ...config } = input;
    const parsed = zHost.parse(config);
    const runtime = parsed.runtime;
    if (!isAbsolute(runtime.executable) || !isAbsolute(runtime.privateDirectory)) throw refused();
    prepareNativePolicy({
      cwd: projectDir,
      privateRoot: runtime.privateRoot,
      privateDirectory: runtime.privateDirectory,
      sandboxMode: parsed.sandboxMode,
    });
    const state = lstatSync(runtime.privateDirectory);
    if (!state.isDirectory() || state.isSymbolicLink() || state.mode & 0o077) throw refused();
    if (process.getuid && state.uid !== process.getuid()) throw refused();
    if (overlaps(realpathSync(runtime.privateDirectory), realpathSync(projectDir))) throw refused();
    if (!lstatSync(realpathSync(runtime.executable)).isFile()) throw refused();
    accessSync(runtime.executable, constants.X_OK);
    codexInstanceEnvironment(runtime.environment, runtime.privateDirectory);
    for (const key of ["HTTP_PROXY", "HTTPS_PROXY"]) {
      const value = runtime.environment[key];
      if (value && (new URL(value).username || new URL(value).password)) throw refused();
    }
    return { ...parsed, approvals: { ...(approvals ?? denyNativeGrants) } };
  } catch {
    throw refused();
  }
}

/** Explicit CLI file only: never search the workspace or a developer's home. */
export function loadManagedCodexHostConfig(
  file: string,
  projectDir: string,
): ManagedCodexHostOptions {
  try {
    if (!isAbsolute(file)) throw refused();
    const stat = lstatSync(file);
    if (!stat.isFile() || stat.isSymbolicLink() || stat.size > 64 * 1024 || stat.mode & 0o077)
      throw refused();
    if (process.getuid && stat.uid !== 0 && stat.uid !== process.getuid()) throw refused();
    if (overlaps(realpathSync(file), realpathSync(projectDir))) throw refused();
    return validateManagedCodexHost(
      zHost.parse(JSON.parse(readFileSync(file, "utf8"))),
      projectDir,
    );
  } catch {
    throw refused();
  }
}

/** Bind one validated envelope to the existing managed session builder; no ambient auth or model fallback. */
export function bindManagedCodexInit(
  host: ManagedCodexHostOptions,
  cmd: SessionInitCommand,
  refreshCredential: NonNullable<
    NonNullable<AgentSessionConfig["managedCodex"]>["runtime"]["refreshCredential"]
  >,
): NonNullable<AgentSessionConfig["managedCodex"]> {
  const profile = cmd.resolvedConfig.agentConfig;
  const credential = parseCodexCredential(cmd.credentials.ai);
  if (
    !profile?.authMode ||
    !["subscription", "api-key"].includes(profile.authMode) ||
    cmd.identity.sessionId !== host.sessionId ||
    profile?.driver !== "codex" ||
    profile.provider !== "openai" ||
    (profile.cloud !== undefined && profile.cloud !== "default") ||
    profile.cloudConfig ||
    !profile.model ||
    !host.subpromptModels.available.includes(profile.model) ||
    credential.configId !== cmd.resolvedConfig.aiProviderConfigId ||
    (profile.authMode === "subscription") !== (credential.kind === "codex-subscription")
  )
    throw refused();
  const reasoningEffort = profile.effort ?? host.reasoningEffort;
  if (!["minimal", "low", "medium", "high", "xhigh"].includes(reasoningEffort)) throw refused();
  return {
    runtime: {
      ...host.runtime,
      credential,
      ...(credential.kind === "codex-subscription" ? { refreshCredential } : {}),
    },
    approvals: host.approvals ?? denyNativeGrants,
    requiredTools: [...host.requiredTools],
    approvalPolicy: host.approvalPolicy,
    sandboxMode: host.sandboxMode,
    reasoningEffort: reasoningEffort as NonNullable<
      AgentSessionConfig["managedCodex"]
    >["reasoningEffort"],
    subpromptModels: { ...host.subpromptModels, available: [...host.subpromptModels.available] },
  };
}
